import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { BuildingRightsType } from "by/gamefactory/cityversetycoon/networking/data/responses/user/BuildingRightsType";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { gsap } from "gsap";
import { IDestroyOptions, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { GameFont } from "../../../common/GameFont";
import { ButtonWithScaleAndPosYAnimation } from "../../../common/components/ButtonWithScaleAndPosYAnimation";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { IFocusable } from "../../../layers/IFocusable";
import { UserProfileResourcePackage } from "../../userprofile/resources/UserProfileResourcePackage";
import { BuildingRankUpBottomTextComponent } from "../components/BuildingRankUpBottomTextComponent";
import { BuildingRankUpLandlordRewardComponent } from "../components/BuildingRankUpLandlordRewardComponent";
import { BuildingRankUpRenterRewardComponent } from "../components/BuildingRankUpRenterRewardComponent";
import { BuildingRankUpRewardComponent } from "../components/BuildingRankUpRewardComponent";
import { BuildingRankUpTopTextComponent } from "../components/BuildingRankUpTopTextComponent";
import { IBuildingRankUpController } from "../controller/IBuildingRankUpController";
import { IBuildingRankUpModel } from "../model/IBuildingRankUpModel";
import { BuildingRankUpResourcePackage } from "../resources/BuildingRankUpResourcePackage";

export class BuildingRankUpPopupView extends BaseScalableView<void> implements IFocusable {
    public readonly focusable: boolean = true;

    private static readonly GREAT_JOB_TEXT = 'GREAT JOB!\nYOUR BUILDING HAS REACHED';
    private static readonly RANK_TEXT = 'RANK ';
    private static readonly CLAIM_BUTTON_TEXT = 'CLAIM REWARD';
    private static readonly OK_BUTTON_TEXT = 'OK';

    private readonly commonResources: CommonResourcesPackage;
    private readonly rankUpResources: BuildingRankUpResourcePackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly role: BuildingRightsType;
    private readonly guestModel: IGuestModel;
    private readonly playerModel: IPlayerModel;
    private readonly buildingRankUpModel: IBuildingRankUpModel;
    private readonly buildingRankUpController: IBuildingRankUpController;
    private readonly landlordAvatar: Texture;

    private fadeScreen: FadeScreen;
    private greenLight: Sprite;
    private stars: Sprite;
    private leftYellowLight: Sprite;
    private rightYellowLight: Sprite;
    private bottomYellowLight: Sprite;
    private rankUpIcon: Sprite;
    private greatJobText: Text;
    private rankText: Text;
    private topTextComponent: BuildingRankUpTopTextComponent;
    private bottomTextComponent: BuildingRankUpBottomTextComponent;
    private claimButton: ButtonWithScaleAndPosYAnimation;
    private renterRewardComponent: BuildingRankUpRenterRewardComponent;
    private landlordRewardComponent: BuildingRankUpLandlordRewardComponent;
    private rewardComponent: BuildingRankUpRewardComponent;
    private animation: gsap.core.Timeline;
    private claimAnimation: gsap.core.Timeline;
    private flag: boolean;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        canvasScaler: ICanvasScaler,
        commonResources: CommonResourcesPackage,
        rankUpResources: BuildingRankUpResourcePackage,
        userProfileResources: UserProfileResourcePackage,
        guestModel: IGuestModel,
        playerModel: IPlayerModel,
        buildingRankUpModel: IBuildingRankUpModel,
        buildingRankUpController: IBuildingRankUpController,
        landlordAvatar: Texture
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.rankUpResources = rankUpResources;
        this.userProfileResources = userProfileResources;
        this.guestModel = guestModel;
        this.playerModel = playerModel;
        this.buildingRankUpModel = buildingRankUpModel;
        this.buildingRankUpController = buildingRankUpController;
        this.landlordAvatar = landlordAvatar;
        this.role = playerModel.buildingRights.value;
        this.animation = new gsap.core.Timeline();
        this.claimAnimation = new gsap.core.Timeline();
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.claimButton.onUp.add(this.claimButtonClickHandle, this);
    }

    public async start(): Promise<void> {
        this.show();
    }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.3, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";

        this.greenLight = new Sprite(this.rankUpResources.assets_views_building_rank_up_green_top_light_png);
        this.stars = new Sprite(this.rankUpResources.assets_views_building_rank_up_stars_png);
        this.leftYellowLight = new Sprite(this.rankUpResources.assets_views_building_rank_up_yellow_light_vertical_png);
        this.rightYellowLight = new Sprite(this.rankUpResources.assets_views_building_rank_up_yellow_light_vertical_png);
        this.rightYellowLight.scale.x = -1;
        this.bottomYellowLight = new Sprite(this.rankUpResources.assets_views_building_rank_up_yellow_light_horizontal_png);
        this.rankUpIcon = new Sprite(this.rankUpResources.assets_views_building_rank_up_rank_up_png);
        this.greatJobText = new Text(BuildingRankUpPopupView.GREAT_JOB_TEXT, new HeaderTextStyle());
        this.greatJobText.skew.x = -0.08;
        this.greatJobText.angle = -5;
        this.rankText = new Text(BuildingRankUpPopupView.RANK_TEXT + this.buildingRankUpModel.level, new RankTextStyle());
        this.rankText.skew.x = -0.08;
        this.rankText.angle = -5;
        this.topTextComponent = new BuildingRankUpTopTextComponent(this.role);
        this.bottomTextComponent = new BuildingRankUpBottomTextComponent(this.role);

        this.claimButton = new ButtonWithScaleAndPosYAnimation(this.commonResources.assets_common_ui_button_green_png);
        const buttonText = new Text(this.buildingRankUpModel.fromUpgrade ? BuildingRankUpPopupView.CLAIM_BUTTON_TEXT : BuildingRankUpPopupView.OK_BUTTON_TEXT, new ButtonTextStyle());
        buttonText.position.set(this.claimButton.width * 0.5 - buttonText.width * 0.5, this.claimButton.height * 0.5 - buttonText.height * 0.5);
        this.claimButton.defaultView.addChild(buttonText);

        this.addChild(
            this.fadeScreen,
            this.greenLight,
            this.leftYellowLight,
            this.rightYellowLight,
            this.bottomYellowLight,
            this.stars,
            this.rankUpIcon,
            this.greatJobText,
            this.rankText,
            this.topTextComponent,
            this.bottomTextComponent,
            this.claimButton,
        );
        this.rewardComponent = new BuildingRankUpRewardComponent(
            this.buildingRankUpModel.totalRewardInCoins,
            this.buildingRankUpModel.additionalPiggyBankTokensMultiplierPercentage,
            this.rankUpResources,
            this.commonResources,
            this.role
        );
        this.addChild(this.rewardComponent)
        switch (this.role) {
            case BuildingRightsType.OWNER:
                break;
            case BuildingRightsType.LANDLORD: {
                this.landlordRewardComponent = new BuildingRankUpLandlordRewardComponent(
                    this.rankUpResources,
                    this.userProfileResources,
                    this.guestModel.avatarTexture.value,
                    this.buildingRankUpModel.currentGuestRewardInCoins,
                    this.playerModel,
                    this.canvasScaler
                );
                this.addChild(this.landlordRewardComponent);
                this.landlordRewardComponent.visible = !this.buildingRankUpModel.fromUpgrade;
                break;
            }
            case BuildingRightsType.RENTER: {
                this.renterRewardComponent = new BuildingRankUpRenterRewardComponent(
                    this.rankUpResources,
                    this.userProfileResources,
                    this.guestModel.avatarTexture.value,
                    this.landlordAvatar,
                    this.buildingRankUpModel.landlordRewardInCoins,
                    this.buildingRankUpModel.currentGuestRewardInCoins,
                    this.playerModel,
                    this.canvasScaler
                );
                this.renterRewardComponent.onLandlordInfoButtonClick.add(this.landlordInfoButtonClickHandle, this);
                this.renterRewardComponent.onRenterInfoButtonClick.add(this.renterInfoButtonClickHandle, this);
                this.addChild(this.renterRewardComponent);
                this.renterRewardComponent.visible = !this.buildingRankUpModel.fromUpgrade;
                break;
            }
        }
        this.rewardComponent.visible = false;
        this.topTextComponent.visible = !this.buildingRankUpModel.fromUpgrade;
        this.bottomTextComponent.visible = !this.buildingRankUpModel.fromUpgrade;
        if (this.role == BuildingRightsType.OWNER) {
            this.topTextComponent.visible = false;
            this.bottomTextComponent.visible = false;
        }
    }

    private layout(width: number, height: number): void {
        this.claimButton.position.set(229, 1375);
        this.bottomYellowLight.position.set(57, 1569);
        this.rankUpIcon.position.set(108, 180);
        this.rewardComponent.position.set(253.5, 815);
        switch (this.role) {
            case BuildingRightsType.OWNER:
                this.stars.position.set(0, -50);
                this.leftYellowLight.position.set(0, 508);
                this.rightYellowLight.position.set(width, 508);
                this.greatJobText.position.set(79, 509);
                this.rankText.position.set(297, 626);
                this.topTextComponent.position.set(width * 0.5 - this.topTextComponent.width * 0.5, 795);
                this.bottomTextComponent.position.set(width * 0.5 - this.bottomTextComponent.width * 0.5, 1607);
                break;
            case BuildingRightsType.LANDLORD: {
                this.stars.position.set(0, -50);
                this.leftYellowLight.position.set(0, 508);
                this.rightYellowLight.position.set(width, 508);
                this.greatJobText.position.set(79, 509);
                this.rankText.position.set(297, 626);
                this.topTextComponent.position.set(width * 0.5 - this.topTextComponent.width * 0.5, 795);
                this.bottomTextComponent.position.set(width * 0.5 - this.bottomTextComponent.width * 0.5, 1607);
                this.landlordRewardComponent.position.set(272, 920);
                break;
            }
            case BuildingRightsType.RENTER: {
                this.leftYellowLight.position.set(0, 508);
                this.rightYellowLight.position.set(width, 508);
                this.greatJobText.position.set(72, 523);
                this.rankText.position.set(292, 640);
                this.topTextComponent.position.set(width * 0.5 - this.topTextComponent.width * 0.5, 783);
                this.bottomTextComponent.position.set(width * 0.5 - this.bottomTextComponent.width * 0.5, 1584);
                this.renterRewardComponent.position.set(198, 957);
                break;
            }
        }
    }

    private async show(): Promise<void> {
        this.fadeScreen.fadeIn();
        this.animation.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.3, ease: "power1.in" });
        if (this.buildingRankUpModel.fromUpgrade || this.role === BuildingRightsType.OWNER) {
            this.animation.fromTo(this.rewardComponent, { alpha: 0 }, { alpha: 1, onStart: () => this.rewardComponent.visible = true, duration: 0.5 }, ">");
        } else {
            this.animation.add(() => {
                if (this.role === BuildingRightsType.RENTER) {
                    this.renterRewardComponent.playAvatarsAnimations();
                }
                if (this.role === BuildingRightsType.LANDLORD) {
                    this.landlordRewardComponent.playAvatarAnimation();
                }
            });
        }
        await this.animation.play();
        this.animation.clear();
    }

    private async hide(): Promise<void> {
        this.animation.to(this, { alpha: 0, duration: 0.3, ease: "power1.out" });
        await this.animation.play();
        this.animation.clear();
    }

    private async claimButtonClickHandle(): Promise<void> {
        this.claimButton.enabled = false;
        if (!this.buildingRankUpModel.fromUpgrade) {
            this.hideShaders();
            await this.hide();
            this.operationControls.complete();
            return;
        }
        if (!this.flag && this.role != BuildingRightsType.OWNER) {
            await this.claimAnimation.fromTo(this.rewardComponent, { alpha: 1 }, {
                alpha: 0, duration: 0.5, onComplete: () => {
                    this.topTextComponent.visible = true;
                    this.bottomTextComponent.visible = true;
                }
            });
            this.claimAnimation.fromTo(this.topTextComponent, { alpha: 0 }, {
                alpha: 1,
                duration: 0.5,
                onStart: () => {
                    this.animateRewardComponentBasedOnRole();
                }
            }, ">");
            this.claimAnimation.fromTo(this.bottomTextComponent, { alpha: 0 }, {
                alpha: 1,
                duration: 0.5,
                onComplete: () => {
                    if (this.role === BuildingRightsType.RENTER) {
                        this.renterRewardComponent.playAvatarsAnimations();
                    }
                    if (this.role === BuildingRightsType.LANDLORD) {
                        this.landlordRewardComponent.playAvatarAnimation();
                    }
                    this.claimButton.enabled = true;
                }
            }, "<");
            await this.claimAnimation.play();
            this.claimAnimation?.kill();
            this.flag = true;
            return;
        }
        this.buildingRankUpController.collectCoins(this.buildingRankUpModel.currentGuestRewardInCoins);
        this.hideShaders();
        await this.hide();
        this.operationControls.complete();
    }

    private hideShaders(): void {
        if (this.role === BuildingRightsType.RENTER) {
            this.renterRewardComponent.hide();
        }
        if (this.role === BuildingRightsType.LANDLORD) {
            this.landlordRewardComponent.hide();
        }
    }

    private animateRewardComponentBasedOnRole(): void {
        const roleToComponentMap: { [key in BuildingRightsType]?: BuildingRankUpLandlordRewardComponent | BuildingRankUpRenterRewardComponent } = {
            [BuildingRightsType.LANDLORD]: this.landlordRewardComponent,
            [BuildingRightsType.RENTER]: this.renterRewardComponent
        };

        const targetComponent = roleToComponentMap[this.role];

        if (targetComponent) {
            const animationTimeLine = new gsap.core.Timeline();
            targetComponent.visible = true;
            animationTimeLine.fromTo(targetComponent, { alpha: 0 }, { alpha: 1, duration: 0.5 }, "<");
        }
    }

    private async landlordInfoButtonClickHandle(): Promise<void> {
        this.bottomTextComponent.visible = false;
        await this.buildingRankUpController.landlordRenterInfoPopup();
        this.bottomTextComponent.visible = true;
    }

    private async renterInfoButtonClickHandle(): Promise<void> {
        this.bottomTextComponent.visible = false;
        await this.buildingRankUpController.landlordRenterInfoPopup();
        this.bottomTextComponent.visible = true;
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.renterRewardComponent?.onLandlordInfoButtonClick.remove(this.landlordInfoButtonClickHandle, this);
        this.renterRewardComponent?.onRenterInfoButtonClick.remove(this.renterInfoButtonClickHandle, this);
        this.claimButton.onUp.remove(this.claimButtonClickHandle, this);
        this.animation?.kill();
        this.claimAnimation?.kill();
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 78,
            lineHeight: 67,
        }));
    }
}

class RankTextStyle extends HeaderTextStyle {
    public constructor() {
        super();
        this.fill = [
            "#53e18c",
            "#33c09e",
            "#25a4be"
        ];
        this.fillGradientStops = [
            0.1,
            0.3,
            1
        ];
        this.fontSize = 112;
        this.fillGradientType = 1;
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 62,
            miterLimit: 0,
            dropShadow: true,
            dropShadowAngle: 0.7,
            dropShadowBlur: 5,
            dropShadowDistance: 3,
            dropShadowAlpha: 0.4
        }));
    }
}
